import api from '@state/api.js';

export default {
	namespaced: true,
	state: {
		currentTask: null,
		currentTaskIndex: -1,
		currentProgression: -1,
		finished: false,
		score: {},
		lastScore: {},
		lastStars: {},
		lastCompleted: {},
		stars: {},
		progressionsLogged: [],
		passesPerProgression: {}
	},
	mutations: {
		SET_CURRENT_TASK(state, newValue) {
			state.currentTask = newValue;
			saveState('progressionTask', newValue);
		},
		CLEAR_CURRENT_TASK(state) {
			state.currentTask = null;
			saveState('progressionTask', null);
		},
		SET_CURRENT_PROGRESSION_INFO(state, {progression, task, finished, stars, score, lastCompleted, lastScore, lastStars, progressionsLogged, passesPerProgression, completedOnDay, nextProgression}) {
			state.currentProgression = progression;
			state.currentTaskIndex = task;
			state.finished = finished;
			state.stars = stars;
			state.score = score;
			state.lastCompleted = lastCompleted;
			state.lastScore = lastScore;
			state.lastStars = lastStars;
			state.progressionsLogged = progressionsLogged;
			state.passesPerProgression = passesPerProgression;
			state.completedOnDay = completedOnDay;
			state.nextProgression = nextProgression;
		},
		SET_SCORE_AND_STARS(state, {taskId, score, stars, day}) {
			state.stars[taskId] = state.stars[taskId] ?? [];
			state.score[taskId] = state.score[taskId] ?? [];
			state.stars[taskId][day] = Math.max(stars, state.stars[taskId][day] ?? 0); 
			state.score[taskId][day] = Math.max(score, state.score[taskId][day] ?? 0);
			state.completedOnDay[taskId] = state.completedOnDay[taskId] ?? [];
			state.completedOnDay[taskId][day] = true;
			state.lastScore[taskId] = score;
			state.lastStars[taskId] = stars;

			state.lastCompleted[taskId] = new Date();
		},
		MOVE_TO_NEXT_PROGRESSION(state) {
			if (state.nextProgression == null) {
				return;
			}
			state.currentProgression = state.nextProgression.progression;
			state.nextProgression = null;
			state.currentTaskIndex = 0;
		},
		SETUP_NEXT_PROGRESSION(state, {progression, date}) {
			state.nextProgression = {
				progression,
				date
			};
		},
		MOVE_TO_TASK(state, task) {
			state.currentTaskIndex = task;
		},
		APPEND_PROGRESSION_DATE(state, data) {
      state.progressionsLogged.push(data);
    }, 
    LOG_PASS_FOR_PROGRESSION(state, {progression, day}) {
    	state.passesPerProgression[progression] = state.passesPerProgression[progression] ?? []; 
    	state.passesPerProgression[progression][day] = (state.passesPerProgression[progression][day] ?? 0) + 1;
    },
    MARK_FINISHED(state) {
			state.finished = true;
		},
		CLEAR_FINISHED(state) {
			state.finished = false;
		}
	},
	getters: {
		getCurrentTask(state) {
			return state.currentTask;
		},
		getProgressionIndex(state) {
			return state.currentProgression;
		},
		getProgressionTaskIndex(state) {
			return state.currentTaskIndex;
		},
		getProgressionStars: state => (taskId, day) => {
      return Math.min(3,(state.stars[taskId.toLowerCase()] ?? [])[day] ?? 0);
    },
    getLastProgressionStars: state => taskId => {
      return state.lastStars[taskId.toLowerCase()] ?? 0;
    },
    getLastProgressionScore: state => taskId => {
      return state.lastScore[taskId.toLowerCase()] ?? 0;
    },
    getLastProgressionPerformance: (state, rootGetters) => taskId => {
      return ((state.lastScore[taskId.toLowerCase()] ?? 0) * 100) / rootGetters['maxPoints'];
    },
    getScore: state => taskId => {
    	return state.lastScore[taskId.toLowerCase()];
    },
    getWasCompletedToday: state => taskId => {
    	if (!state.lastCompleted[taskId.toLowerCase()]) {
    		return false;
    	}
    	let date = state.lastCompleted[taskId.toLowerCase()];
    	if (typeof date == "string") {
    		date = new Date(date);
    	}
    	return sameLocalDay(date, new Date());
    },
    getWasCompletedOnDay: state => (taskId, day) => {
    	if (!state.lastCompleted[taskId.toLowerCase()]) {
    		return false;
    	}
    	return (state.completedOnDay[taskId.toLowerCase()] ?? [])[day] ?? false;
    },
    getNumberOfDays(state) {
      return state.progressionsLogged.length;
    },
    getProgressionsLoggedOnDays(state) {
      return state.progressionsLogged.map(({progressionIndex}) => progressionIndex);
    },
    getDaysPassedForCurrentProgression: (state, getters) => {
    	const day = getters['getCurrentDay'];
    	if (day < 0) {
    		return 0;
    	}
    	let count = 0;
    	const passes = state.passesPerProgression[state.currentProgression] ?? [];
    	for (let i=0; i <= day; i++) {
    		if ((passes[i] ?? 0) > 0) {
    			count ++;
    		}
    	}
    	return count;
    },
    getPassesForProgression: (state, getters) => (progression, day) => {
    	if (getters['getProgressionIndex'] > progression) {
    		return 2;
    	}
    	if (!state.passesPerProgression[progression]) {
    		return 0;
    	}
    	if (typeof state.passesPerProgression[progression] === "number") {
    		return state.passesPerProgression[progression];
    	}

    	const passes = state.passesPerProgression[progression];
    	if (!passes) {
    		return 0;
    	}
    	let count = 0;
    	for (let i=0; i <= day; i++) {
    		if ((passes[i] ?? 0) > 0) {
    			count = count + 1;
    		}
    	} 
    	return Math.min(2, count);
    }, 
    getCurrentDay(state) {
    	const now = new Date();
    	const day = state.progressionsLogged.findIndex(({date}) => sameLocalDay(now, date));
    	return day;
    },
    getTaskDay(state) {
    	return state.progressionsLogged.length - 1;
    },
    shouldProgress(state) {
    	if (!state.nextProgression) {
    		return false;
    	}
    	const now = new Date();
    	const {date} = state.nextProgression;	
    	const progDate = typeof date === "string" ? new Date(date) : date;

    	function startOfDay(d) {
			  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
			}

	  	return startOfDay(progDate) <= startOfDay(now);
    },
    getFinished(state) {
    	return state.finished;
    }
	},
	actions: {
		async init({ commit, dispatch }) {
      await dispatch('fetchProgressions');

    	const val = getSavedState('progressionTask');
    	if (val) {
    		commit('SET_CURRENT_TASK', val);
    	}  
  	},
    setCurrentTask({commit}, newValue) {
			commit('SET_CURRENT_TASK', newValue);
		},
		logProgressionOnDate({state, commit, dispatch, getters, rootGetters}) {
      const now = new Date();
      const match = state.progressionsLogged.some(data => sameLocalDay(now, data.date));
      if (match) {
        return;
      }
      if (state.progressionsLogged.length >= rootGetters['project/getTotalDays']) {
      	return;
      }
      const progressionIndex = getters['getProgressionIndex'];
      commit('APPEND_PROGRESSION_DATE', {date: now, progressionIndex});
      dispatch('saveProgression');
    },
		async fetchProgressions({commit, dispatch, getters, rootGetters}) {
      const projectId = rootGetters['project/getProjectId'];
      if (projectId) {
        try {
        	const data = await api.get(`/api/progression/${projectId}`).json();
          const {stars = {}, score = {}, lastCompleted = {}, currentProgression = 0, currentTaskIndex = 0, finished = false, lastScore = {}, lastStars = {}, progressionsLogged = [], passesPerProgression = {}, completedOnDay = {}, nextProgression} = data ?? {};
          
          commit('SET_CURRENT_PROGRESSION_INFO', {stars, score, lastCompleted, finished, progression: currentProgression, task: currentTaskIndex, lastScore, lastStars, progressionsLogged, passesPerProgression, completedOnDay, nextProgression});

          if (progressionsLogged.length == 0) {
          	dispatch('logProgressionOnDate');
          }

          if (getters['shouldProgress'] && currentProgression < rootGetters['project/getNumberOfProgressions'] - 1) {						
          	commit('MOVE_TO_NEXT_PROGRESSION');
          	dispatch('logProgressionOnDate');
						dispatch('saveProgression');
          }
        } catch (e) {
          console.dir(e);
        }
     	}
		},
		awardStars({commit, dispatch}, {taskId, score, starThresholds, day}) {
			const numStars = starThresholds.reduce((sum, thr) => score >= thr ? sum + 1 : sum, 0);
			commit('SET_SCORE_AND_STARS', {taskId: taskId.toLowerCase(), score, stars: numStars, day});
      dispatch('saveProgressionInfo', {taskId: taskId.toLowerCase()});
		},
		async checkIfFinished({state, commit, getters, rootGetters, dispatch}) {
			if (!rootGetters['project/hasProgressionsEnabled']) {
				return;
			}
			const {tasks} = rootGetters['project/getProgressionData'](state.currentProgression);
			const day = getters['getCurrentDay'];
 			if (day == -1 && state.progressionsLogged.length >= rootGetters['project/getTotalDays']) {
      	commit('MARK_FINISHED');
				await dispatch('saveProgression');
		
      	return;
      }

			const maxDays = rootGetters['project/getTotalDays'];
			if (day >= maxDays - 1 && tasks.length - 1 == state.currentTaskIndex) {
				commit('MARK_FINISHED');
				await dispatch('saveProgression');
				return;
			}

			if (state.finished) {
				commit('CLEAR_FINISHED');
			}
		},
		async checkProgression({state, commit, getters, dispatch, rootGetters}) {
			if (!rootGetters['project/hasProgressionsEnabled']) {
				return;
			}
			const {rules, threshold, days} = rootGetters['project/getProgressionData'](state.currentProgression);
			const numPassed = rules.reduce((count, {comparitor, game, thresholdScore}) => {
				const completedToday = getters['getWasCompletedToday'](game._id);
				if (!completedToday) {
					return count;
				}
				const score = getters['getScore'](game._id);
				let pass = false;
				switch (comparitor) {
					case 'lt':
						pass = score < thresholdScore;
						break;
					case 'gt':
						pass = score > thresholdScore;
						break;
				}
				return pass ? count + 1 : count;
			},0);

			if (numPassed < threshold) {
				return;
			}

			const day = getters['getCurrentDay'];
			commit('LOG_PASS_FOR_PROGRESSION', {progression: state.currentProgression, day});

			if (getters['getDaysPassedForCurrentProgression'] >= days) {
				const date = new Date();
				date.setDate(date.getDate() + 1);
				commit('SETUP_NEXT_PROGRESSION', {progression: state.currentProgression + 1, date});
			} 
			
			await dispatch('saveProgression');
		},
		async moveToNextTask({state, commit, dispatch, rootGetters}) {
			if (!rootGetters['project/hasProgressionsEnabled']) {
				return;
			}
			const {tasks} = rootGetters['project/getProgressionData'](state.currentProgression);
			if (state.currentTaskIndex >= tasks.length - 1) {
				return;
			}

			commit('MOVE_TO_TASK', state.currentTaskIndex + 1);
			await dispatch('saveProgression');
		},
		async checkForProgressionReset({ state, getters, commit, dispatch, rootGetters}) {
			const {tasks} = rootGetters['project/getProgressionData'](state.currentProgression);
			const day = getters['getCurrentDay'];
			const completedToday = tasks.reduce((count, {_id}) => {
				return day >= 0 && getters['getWasCompletedOnDay'](_id, day) ? count + 1: count;
			},0);

			if (completedToday == 0) {
				commit('MOVE_TO_TASK', 0);
				await dispatch('saveProgression');
			}
		},
		async saveProgressionInfo({ state, getters, rootState, dispatch, rootGetters }, { taskId }) {
      const projectId = rootGetters['project/getProjectId'];

      const { accessToken: token, userref } = rootState['auth']['currentUser'];

      const value = {
        operation: 'progresion-task-update',
        taskId,
        userref,
        projectId,
        stars: state.stars[taskId],
        score: state.score[taskId],
        lastCompleted: state.lastCompleted[taskId],
        lastScore: state.lastScore[taskId],
        lastStars: state.lastStars[taskId],
        completedOnDay: state.completedOnDay[taskId]
      };

      await dispatch('data/save', { token, value }, { root: true });
    },
    async saveProgression({ state, getters, rootState, dispatch, rootGetters }) {
      const projectId = rootGetters['project/getProjectId'];

      const { accessToken: token, userref } = rootState['auth']['currentUser'];

      const value = {
        operation: 'progresion-update',
        userref,
        projectId,
        currentProgression: state.currentProgression,
        currentTaskIndex: state.currentTaskIndex,
        progressionsLogged: state.progressionsLogged,
        passesPerProgression: state.passesPerProgression,
        nextProgression: state.nextProgression,
        finished: state.finished
      };

      await dispatch('data/save', { token, value }, { root: true });
    }
	}
};

function getSavedState(key) {
  return JSON.parse(window.localStorage.getItem(key));
}

function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state));
}

function sameLocalDay(date1, date2) {
	const a = typeof date1 === "string" ? new Date(date1) : date1;
	const b = typeof date2 === "string" ? new Date(date2) : date2;
	return a.getDate() == b.getDate() && a.getMonth() == b.getMonth() && a.getFullYear() == b.getFullYear();
}